// A reboot or a shutdown from the machine panel (DECISIONS 2026-10-18 "a reboot
// from the interface", "a shutdown from the interface"): what the dialogs say,
// from the node's plan, and when the fleet says "rebooting" or "shut down by X"
// instead of "offline". Logic only; the dialog is MachineDetail's.
import type { RebootPlan, RebootStop } from './api'
import { dateTime } from './util'

// a stop as the node's POST names it: the dialog sends back what it showed
export const stopKey = (s: RebootStop) => `${s.kind}:${s.id}`

// typing the node's name is for when something is outputting; viewers and
// work with nothing going out are only listed
export const mustType = (p: RebootPlan): boolean => p.stops.length > 0

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// what the plan says is lost, as the shutdown and the reboot both say it: the
// stops in the danger colour, the plain lines under them
function lost(p: RebootPlan): { stops: string[]; plain: string[] } {
    const plain: string[] = []
    const { viewers, relays } = p.watching
    const seen = [
        viewers > 0 && count(viewers, 'monitoring viewer', 'monitoring viewers'),
        relays > 0 && count(relays, 'relay', 'relays'),
    ].filter((x): x is string => !!x)
    if (seen.length)
        plain.push(`${seen.join(' and ')} ${viewers + relays === 1 ? 'loses their' : 'lose their'} picture`)
    const { encodes, inputs } = p.running
    const run = [
        encodes > 0 && count(encodes, 'encode', 'encodes'),
        inputs > 0 && count(inputs, 'input', 'inputs'),
    ].filter((x): x is string => !!x)
    if (run.length) plain.push(`${run.join(' and ')} ${encodes + inputs === 1 ? 'stops' : 'stop'}`)
    return { stops: p.stops.map((s) => `${s.name} stops`), plain }
}

// rebootLines: the stops in the danger colour, the plain lines under them,
// the 1+1 warning and the update hold's ('' when none); a held node reboots
// all the same, the hold is only said
export function rebootLines(
    p: RebootPlan,
    partner: { name: string; online: boolean } | null,
): { stops: string[]; plain: string[]; partner: string; hold: string } {
    return {
        ...lost(p),
        partner:
            partner && !partner.online
                ? `${partner.name} is offline: this feed has no other side while the node reboots`
                : '',
        hold: p.hold
            ? `Updates are held on this node${p.hold.note ? `: ${p.hold.note}` : ''}${p.hold.until ? `, until ${dateTime(p.hold.until)}` : ''}`
            : '',
    }
}

// rebootingNow: a node a reboot was asked of, offline inside the window central
// holds for it; an online node is never shown rebooting
export function rebootingNow(n: { online: boolean; rebooting_until?: string | null }, now: number): boolean {
    return !n.online && !!n.rebooting_until && Date.parse(n.rebooting_until) > now
}

// rootRefusal: root's last answer says a refusal only while it is recent: its
// status file stays for days, an old "refused" is not this ask's
export const ROOT_FRESH_MS = 2 * 60_000
export function rootRefusal(p: RebootPlan, now: number): string {
    const r = p.root
    if (!r || r.state !== 'refused' || !r.reason) return ''
    const at = Date.parse(r.at)
    return Number.isFinite(at) && now - at <= ROOT_FRESH_MS ? r.reason : ''
}

// rootAction: what root's last answer was for, in the words of the panel
// (a missing action is a reboot: a request from before shutdowns)
export const rootAction = (p: RebootPlan): 'shutdown' | 'reboot' =>
    p.root?.action === 'poweroff' ? 'shutdown' : 'reboot'

// placeText: where a node is, as central's store has it ('' when it does not)
export const placeText = (n: { city?: string; country?: string } | null): string =>
    [n?.city, n?.country].filter((x): x is string => !!x).join(', ')

// shutdownLines: what the shutdown dialog says. The warning first: the node
// stays off until a person acts, at its place when known; a cloud node's VM
// stops and is started again from central's Cloud page or the provider's
// console. The stops and plain lines are the reboot's; the update hold is not
// said (a node that is off is not updated, and the hold means nothing to it).
export function shutdownLines(
    p: RebootPlan,
    partner: { name: string; online: boolean } | null,
    cloud: string | null,
    place: string,
): { warning: string; stops: string[]; plain: string[]; partner: string } {
    return {
        warning: cloud
            ? `The VM on ${cloud} stops and stays stopped until someone starts it again, from central’s Cloud page or the provider’s console`
            : `The node stays off until someone powers it on${place ? `, at ${place}` : ''}`,
        ...lost(p),
        partner:
            partner && !partner.online
                ? `${partner.name} is offline: this feed has no other side while the node is off`
                : '',
    }
}

// shutDownBy: who shut an offline node down, '' when nobody did. Central's rule
// (shutdownGrace, internal/central/nodes.go): a node counts as shut down only
// when it was last seen within 10 minutes of the shutdown event; a mark whose
// shutdown never happened must not silence a later outage. Never for an online node.
export const SHUTDOWN_GRACE_MS = 10 * 60_000
export function shutDownBy(n: {
    online: boolean
    shutdown_by?: string | null
    shutdown_at?: string | null
    last_seen?: string | null
}): string {
    if (n.online || !n.shutdown_by || !n.shutdown_at || !n.last_seen) return ''
    const at = Date.parse(n.shutdown_at)
    const seen = Date.parse(n.last_seen)
    return Number.isFinite(at) && Number.isFinite(seen) && seen <= at + SHUTDOWN_GRACE_MS ? n.shutdown_by : ''
}

// offlineWord: what an offline node is called: "shut down by X" (it wins), then
// "rebooting", else "offline". Never green, never an alarm of its own here.
export function offlineWord(
    n: {
        online: boolean
        rebooting_until?: string | null
        shutdown_by?: string | null
        shutdown_at?: string | null
        last_seen?: string | null
    },
    now: number,
): string {
    const by = shutDownBy(n)
    if (by) return `shut down by ${by}`
    return rebootingNow(n, now) ? 'rebooting' : 'offline'
}

// openAlarmCount: the alarms the fleet counts for a node: its own open ones, and
// one for being offline, except while central holds that alarm back (shut down
// by someone, or inside a reboot's window)
export function openAlarmCount(n: Parameters<typeof offlineWord>[0], open: number, now: number): number {
    return open + (n.online || shutDownBy(n) || rebootingNow(n, now) ? 0 : 1)
}

// latestAsk: a run of asks where only the newest one's answer may act. begin()
// starts an ask and returns its check, true while no later ask has begun. The
// machine panel opens its dialog through one: a slow open of an earlier click
// (Shut down) must not turn the dialog of a later one (Reboot) into its action,
// with the name already typed.
export function latestAsk(): { begin: () => () => boolean } {
    let seq = 0
    return {
        begin: () => {
            const mine = ++seq
            return () => mine === seq
        },
    }
}
